<template>
  <!-- Off-screen render host: html2canvas captures #waiver-pdf-root into one continuous
       canvas, we composite the signature onto it at the box's exact pixels, then slice to
       PDF pages. 760px wide = the design width; per-page margins applied at slice time. -->
  <div aria-hidden="true"
       style="position: fixed; left: -9999px; top: 0; width: 760px; background: #ffffff; z-index: -1;">
    <div id="waiver-pdf-root" style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #16161d;">

        <!-- Header: identity + generated -->
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center;">
            <img v-if="logoSrc" :src="logoSrc" style="height: 32px; max-width: 118px; object-fit: contain; margin-right: 12px;" alt="" />
            <div style="font-size: 11px; letter-spacing: 1.6px; text-transform: uppercase; color: #9499a3; font-weight: 700;">{{ groupName }}</div>
          </div>
          <div style="font-size: 10px; color: #9499a3;">Generated {{ generatedAt }}</div>
        </div>

        <!-- Title block -->
        <div style="margin-top: 30px;">
          <div style="font-size: 10px; letter-spacing: 2.2px; text-transform: uppercase; color: #e14eca; font-weight: 700;">Waiver record</div>
          <div style="font-size: 27px; font-weight: 700; color: #16161d; letter-spacing: -0.4px; margin-top: 7px;">{{ signerName || 'Waiver' }}</div>
        </div>
        <div style="height: 1px; background: #e8e8ee; margin-top: 20px;"></div>

        <!-- Metadata: label-over-value grid -->
        <div style="margin-top: 24px; background: #fafbfc; border: 1px solid #eeeef3; border-radius: 10px; padding: 22px 24px 8px;">
          <div style="display: flex; flex-wrap: wrap;">
            <div v-for="m in metaItems" :key="m.label" style="width: 33.33%; margin-bottom: 16px; padding-right: 12px; box-sizing: border-box;">
              <div style="font-size: 9px; letter-spacing: 1px; text-transform: uppercase; color: #9499a3; font-weight: 700; margin-bottom: 5px;">{{ m.label }}</div>
              <div style="font-size: 12.5px; color: #16161d; word-break: break-word;">{{ m.value }}</div>
            </div>
          </div>
        </div>

        <!-- Agreement -->
        <div v-if="waiverHtml" style="margin-top: 30px;">
          <div style="display: flex; align-items: center; margin-bottom: 12px;">
            <span style="width: 14px; height: 2px; background: #e14eca; display: inline-block; margin-right: 8px;"></span>
            <span style="font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #9499a3; font-weight: 700;">Agreement</span>
          </div>
          <div class="wpdf-agreement" v-html="waiverHtml" style="font-size: 10.5px; line-height: 1.75; color: #4b5563;"></div>
        </div>

        <!-- Consent -->
        <div v-if="consentRows.length" style="margin-top: 30px;">
          <div style="display: flex; align-items: center; margin-bottom: 12px;">
            <span style="width: 14px; height: 2px; background: #e14eca; display: inline-block; margin-right: 8px;"></span>
            <span style="font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #9499a3; font-weight: 700;">Consent</span>
          </div>
          <div>
            <span
              v-for="c in consentRows"
              :key="c.label"
              :style="`display: inline-block; font-size: 11px; padding: 5px 12px; border-radius: 14px; margin: 0 8px 8px 0; border: 1px solid ${c.optedOut ? '#fecaca' : '#bbf7d0'}; background: ${c.optedOut ? '#fef2f2' : '#f0fdf4'}; color: ${c.optedOut ? '#b91c1c' : '#15803d'};`"
            >{{ c.label }}: {{ c.value }}</span>
          </div>
        </div>

        <!-- Additional information -->
        <div v-if="customRows.length" style="margin-top: 30px;">
          <div style="display: flex; align-items: center; margin-bottom: 12px;">
            <span style="width: 14px; height: 2px; background: #e14eca; display: inline-block; margin-right: 8px;"></span>
            <span style="font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #9499a3; font-weight: 700;">Additional information</span>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <tbody>
              <tr v-for="(f, i) in customRows" :key="i">
                <td style="padding: 8px 0; color: #6b7280; width: 210px; vertical-align: top; border-bottom: 1px solid #f0f0f4;">{{ f.label }}</td>
                <td style="padding: 8px 0; color: #16161d; border-bottom: 1px solid #f0f0f4;">{{ f.response }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Signature flows naturally at the end. renderPdf composites the signature onto
             the continuous canvas at this box's pixels, then avoids slicing through this
             section (#id used to push it whole to the next page if a page break would cut it). -->
        <div id="waiver-pdf-sig-section" style="margin-top: 32px;">
          <div style="display: flex; align-items: center; margin-bottom: 12px;">
            <span style="width: 14px; height: 2px; background: #e14eca; display: inline-block; margin-right: 8px;"></span>
            <span style="font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #9499a3; font-weight: 700;">Signature</span>
          </div>
          <div v-if="signatureSrc" style="display: inline-block; border: 1px solid #e8e8ee; border-radius: 10px; padding: 16px 20px; background: #ffffff;">
            <canvas ref="sigCanvas" id="waiver-pdf-signature-canvas" :style="{ width: sigW + 'px', height: sigH + 'px', display: 'block' }" role="img" :aria-label="signatureAlt"></canvas>
          </div>
          <div v-else style="font-size: 11px; color: #9499a3;">
            {{ notParticipating ? 'No signature required.' : 'No signature on file.' }}
          </div>
          <div v-if="signedText" style="font-size: 11px; color: #6b7280; margin-top: 12px;">{{ signedText }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { sanitizeQuillHtml } from '@/util/quillSanitize'
import { renderSignature } from '@/util/waiverSignature'
import { date as fmtWallDate, time as fmtWallTime, formatGroupDate, formatGroupTime } from '@/plugins/dates'

const record = ref<any>({ waiver: {}, consent_fields: [], custom_field_responses: [] })
const logoSrc = ref('')
const sigCanvas = ref<HTMLCanvasElement | null>(null)
const sigW = ref(240)
const sigH = ref(92)
const generatedAt = ref('')

function getGroup() {
  try {
    return JSON.parse(localStorage.getItem('group') || '{}') || {}
  } catch (e) {
    return {}
  }
}

const waiver = computed(() => record.value.waiver || {})

const groupName = computed(() => getGroup().name || 'Off The Couch')

const signerName = computed(() => {
  const w = waiver.value
  const first = w.display_first_name || w.first_name
  const last = w.display_last_name || w.last_name
  const name = [first, last].filter(Boolean).join(' ').trim()
  return name || w.email || ''
})
const email = computed(() => waiver.value.email || '')
const gameName = computed(() => waiver.value.game_name || waiver.value.game || '')
const isMinor = computed(() => !!Number(waiver.value.is_minor))
const notParticipating = computed(() => !!Number(waiver.value.not_participating))

const eventDateText = computed(() => {
  const w = waiver.value
  if (!w.booking_date) return ''
  const g = getGroup()
  // booking_date / start_time are venue wall-clock — format without tz conversion.
  const d = fmtWallDate(w.booking_date, g.date_format || 'MM/DD/YYYY')
  if (Number(w.is_all_day) === 1) {
    return w.all_day_text ? `${d} · ${w.all_day_text}` : d
  }
  const t = w.start_time ? fmtWallTime(w.start_time, g.time_format) : ''
  return t ? `${d} at ${t}` : d
})

const metaItems = computed(() => [
  { label: 'Signer', value: signerName.value || '—' },
  { label: 'Event', value: gameName.value || 'Walk-in' },
  { label: 'Date', value: eventDateText.value || '—' },
  { label: 'Status', value: notParticipating.value ? 'Not participating' : 'Participating' },
  { label: 'Minor', value: isMinor.value ? 'Yes' : 'No' },
  { label: 'Email', value: email.value || '—' },
])

const signedText = computed(() => {
  const w = waiver.value
  const ts = w.created_time
  if (!ts) return ''
  // created_time is a UTC timestamp — group-tz aware formatting.
  const d = formatGroupDate(ts)
  const t = formatGroupTime(ts)
  const who = isMinor.value ? 'guardian' : signerName.value
  const stamp = t ? `${d} at ${t}` : d
  return who ? `Signed by ${who} on ${stamp}` : `Signed on ${stamp}`
})

const waiverHtml = computed(() => {
  const text = waiver.value.waiver_text
  return text ? sanitizeQuillHtml(text) : ''
})

const signatureSrc = computed(() => {
  const sig = waiver.value.signature
  return sig ? renderSignature(sig) : ''
})
const signatureAlt = computed(() =>
  isMinor.value ? `Guardian signature${signerName.value ? ' for ' + signerName.value : ''}` : `Signature${signerName.value ? ' of ' + signerName.value : ''}`
)

// Consent fields: the single-GET returns the consent fields active at sign time
// ({ name, label, is_optin }); the waiver row carries the actual opt-out answers.
const consentRows = computed(() => {
  const w = waiver.value
  const fields = record.value.consent_fields || []
  return fields.map((f: any) => {
    let optedOut = false
    if (f.name === 'Email Opt Out') optedOut = !!Number(w.email_optout)
    else if (f.name === 'Photo Opt Out') optedOut = !!Number(w.picture_optout)
    return { label: f.label || f.name, value: optedOut ? 'Opted out' : 'Opted in', optedOut }
  })
})

const customRows = computed(() =>
  (record.value.custom_field_responses || []).map((r: any) => ({
    label: r.label || '',
    response: r.response != null ? String(r.response) : '',
  }))
)

const filename = computed(() => {
  const name = (signerName.value || 'waiver').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  const id = waiver.value.id ? `-${waiver.value.id}` : ''
  return `waiver-${name}${id}`
})

// Convert a (possibly cross-origin) image URL to a data URL so html2canvas never
// taints the canvas. Returns '' on any failure — the PDF simply omits the logo.
function safeImageToDataUrl(url: string): Promise<string> {
  return new Promise((resolve) => {
    if (!url) return resolve('')
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve('')
        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      } catch (e) {
        resolve('')
      }
    }
    img.onerror = () => resolve('')
    img.src = url
  })
}

// Draw the signature onto the on-page <canvas>. html2canvas reads canvas element pixels
// directly (it does NOT go through the image-loading/data-URL path that left a raw <img>
// blank), so this renders reliably. Buffer is the signature's natural resolution; the
// element is CSS-scaled into a 280×92 box. White fill = no transparency for the export.
function drawSignatureToCanvas(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const canvas = (sigCanvas.value || document.getElementById('waiver-pdf-signature-canvas')) as HTMLCanvasElement | null
    if (!src || !canvas) return resolve(false)
    const img = new Image()
    img.onload = () => {
      try {
        const natW = img.naturalWidth || 600
        const natH = img.naturalHeight || 200
        canvas.width = natW
        canvas.height = natH
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve(false)
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, natW, natH)
        ctx.drawImage(img, 0, 0, natW, natH)
        let dh = 92
        let dw = natH ? (dh * natW) / natH : 240
        if (dw > 280) {
          dw = 280
          dh = natW ? (dw * natH) / natW : 92
        }
        sigW.value = Math.round(dw)
        sigH.value = Math.round(dh)
        resolve(true)
      } catch (e) {
        resolve(false)
      }
    }
    img.onerror = () => resolve(false)
    img.src = src
  })
}

// Per-page PDF margins (inches). A4 paper is a fixed format; content height is not.
const PDF_MARGIN = { top: 0.55, left: 0.6, bottom: 0.75, right: 0.6 }
const A4 = { w: 8.2677, h: 11.6929 }

// Atomic blocks for pagination, in document order. Each top-level section stays whole,
// except the agreement body, which breaks between its paragraphs (so long legal text can
// span pages without keeping the whole block together or splitting mid-line).
function collectAtomicBlocks(root: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = []
  for (const child of Array.from(root.children) as HTMLElement[]) {
    const agreement = child.querySelector('.wpdf-agreement') as HTMLElement | null
    if (agreement) {
      for (const sub of Array.from(child.children) as HTMLElement[]) {
        if (sub === agreement) {
          const paras = Array.from(agreement.children) as HTMLElement[]
          if (paras.length) out.push(...paras)
          else out.push(agreement)
        } else {
          out.push(sub) // section eyebrow
        }
      }
    } else {
      out.push(child) // header, title, metadata, consent, custom, signature section
    }
  }
  return out
}

// Render by COMPOSITING, not coordinate-mapping. html2canvas captures the whole document
// into ONE continuous canvas; we paint the signature onto that canvas at the box's exact
// pixels (measured from the same DOM that produced it, so it physically lands in the box —
// impossible to misalign), then slice the canvas into pages at block boundaries (see
// collectAtomicBlocks) so nothing splits mid-line. Page count derives from the rendered
// canvas — nothing is a fixed height.
async function renderPdf() {
  const root = document.getElementById('waiver-pdf-root')
  if (!root) return
  const SCALE = 2
  const big = await html2canvas(root, { scale: SCALE, useCORS: true, backgroundColor: '#ffffff', logging: false })

  const rootRect = root.getBoundingClientRect()
  const s = big.width / rootRect.width

  // Composite the signature onto the continuous canvas at the box's exact pixels.
  const cv = (sigCanvas.value || document.getElementById('waiver-pdf-signature-canvas')) as HTMLCanvasElement | null
  if (cv) {
    const cRect = cv.getBoundingClientRect()
    const ctx = big.getContext('2d')
    if (ctx) {
      // html2canvas leaves a scale() transform on the returned canvas's context, which
      // would double our destination coords. Reset to identity so we draw at raw pixels.
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.drawImage(cv,
        (cRect.left - rootRect.left) * s, (cRect.top - rootRect.top) * s,
        cRect.width * s, cRect.height * s)
    }
  }

  // Per-page content area, in canvas px.
  const contentW = A4.w - PDF_MARGIN.left - PDF_MARGIN.right
  const pxPerIn = big.width / contentW
  const fullPagePx = (A4.h - PDF_MARGIN.top - PDF_MARGIN.bottom) * pxPerIn

  // Atomic blocks (canvas px) in document order: each section stays whole; the agreement
  // breaks between paragraphs. Page breaks land between blocks so nothing splits mid-line.
  const blocks = collectAtomicBlocks(root).map((el) => {
    const r = el.getBoundingClientRect()
    return { top: (r.top - rootRect.top) * s, bottom: (r.bottom - rootRect.top) * s }
  }).filter((b) => b.bottom > b.top)

  // Decide page slices first (so the footer can read "Page x of N").
  const slices: Array<[number, number]> = []
  let y = 0
  while (y < big.height - 1) {
    let end = Math.min(y + fullPagePx, big.height)
    // If a block straddles the page boundary, end the page at its top so it moves whole to
    // the next page. Blocks are in document order, so the first straddler wins.
    for (const b of blocks) {
      if (b.top > y + 1 && b.top < end - 1 && b.bottom > end + 1) { end = b.top; break }
    }
    if (end <= y) end = Math.min(y + fullPagePx, big.height) // block taller than a page: force-split
    slices.push([y, end])
    y = end
  }
  if (slices.length === 0) slices.push([0, big.height])

  const brand = groupName.value
  const pdf = new (jsPDF as any)('p', 'in', 'a4')
  for (let page = 0; page < slices.length; page++) {
    const [sy, ey] = slices[page]
    const sh = ey - sy
    const pageCanvas = document.createElement('canvas')
    pageCanvas.width = big.width
    pageCanvas.height = Math.max(1, Math.round(sh))
    const pctx = pageCanvas.getContext('2d')
    if (pctx) {
      pctx.fillStyle = '#ffffff'
      pctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
      pctx.drawImage(big, 0, sy, big.width, sh, 0, 0, big.width, sh)
    }
    if (page > 0) pdf.addPage()
    pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', PDF_MARGIN.left, PDF_MARGIN.top, contentW, sh / pxPerIn)
    pdf.setFontSize(8)
    pdf.setTextColor(168, 170, 178)
    pdf.text(brand, 0.5, A4.h - 0.3)
    pdf.text(`Page ${page + 1} of ${slices.length}`, A4.w - 1.4, A4.h - 0.3)
  }
  pdf.save(`${filename.value}.pdf`)
}

/**
 * Generate and download a branded waiver PDF. Returns a promise that resolves once the
 * file has been saved (or generation failed) — use it to drive a loading state.
 * @param data { waiver, consent_fields, custom_field_responses } — typically the object
 *   returned by fetchWaiverDetail(id, { includeSignature: true }).
 */
function generate(data: any): Promise<void> {
  record.value = {
    waiver: (data && data.waiver) || {},
    consent_fields: (data && data.consent_fields) || [],
    custom_field_responses: (data && data.custom_field_responses) || [],
  }
  // Match the group's date format so "Generated" reads consistently with the event date.
  const now = new Date()
  const isoToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  generatedAt.value = fmtWallDate(isoToday, getGroup().date_format || 'MM/DD/YYYY')

  return new Promise<void>((resolve) => {
    ;(async () => {
      try {
        await nextTick()
        const [logo] = await Promise.all([
          safeImageToDataUrl(getGroup().img || ''),
          drawSignatureToCanvas(signatureSrc.value),
        ])
        logoSrc.value = logo
        // Tick + small delay so the logo and layout settle before rasterizing.
        await nextTick()
        await new Promise((r) => setTimeout(r, 60))
        await renderPdf()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Waiver PDF generation error:', e)
      } finally {
        resolve()
      }
    })()
  })
}

defineExpose({ generate })
</script>

<script lang="ts">
export default { name: 'WaiverPdfDocument' }
</script>

<style scoped>
/* OTC-322: normalize the agreement body. The saved waiver HTML can carry a venue's font
   and colors (e.g. white text on a dark background) from the editor; in the PDF that
   renders unreadable. Force readable dark text in the PDF's font on a clean background,
   overriding any inline styling — !important so it beats inline styles, ::v-deep so it
   reaches the v-html-injected nodes. */
.wpdf-agreement ::v-deep * {
  color: #374151 !important;
  background-color: transparent !important;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
}
</style>
